/**
 * AI driver tick for the §15 "CPU opponents and AI" archetypes.
 * The controller stays in progress space and dispatches through
 * `aiArchetypes.ts` for rocket starter, clean line, bully, cautious,
 * chaotic, and enduro behaviour.
 *
 * Source of truth: `docs/gdd/15-cpu-opponents-and-ai.md`. The "AI is
 * simulated in track-progress space, not free-driving 3D rigid bodies"
 * directive is followed here: the AI consumes its own `CarState` (the
 * same kinematic shape the §10 physics step produces) and a compiled
 * track to derive an ideal lateral offset and a target speed per
 * segment, then maps both onto the canonical `Input` shape that the
 * physics step accepts. This keeps the AI decoupled from rendering and
 * lets the same loop drive the player car or any AI car interchangeably.
 *
 * The function is pure:
 *
 *   tickAI(
 *     driver,
 *     aiState,
 *     aiCar,
 *     player,
 *     track,
 *     race,
 *     stats,
 *     context,
 *     dt,
 *     cpuModifiers,
 *     weatherSkillScalar,
 *     visibilityRiskScalar,
 *   )
 *     -> { input, nextAiState }
 *
 * No globals or time source. Same arguments, including `AIState.seed`,
 * produce identical outputs across runs, satisfying the §21
 * replay/ghost determinism requirement (AGENTS.md RULE 8 "Determinism
 * is mandatory"). The `seed` field on `AIState` is the dedicated
 * deterministic PRNG channel for the shared AI mistake hook.
 *
 * §23 "CPU difficulty modifiers" wiring: `tickAI` accepts an optional
 * `cpuModifiers` parameter (the §15-tier scalars from
 * `aiDifficulty.getCpuModifiers`) and stacks all three scalar columns
 * on top of per-driver AI data. `paceScalar` raises or lowers target
 * speed, `recoveryScalar` scales the light catch-up term when an AI
 * trails the player, and `mistakeScalar` scales the per-driver
 * `mistakeRate` for deterministic lane-target mistakes. Defaults to a
 * legacy identity row so existing callers keep behavior unchanged.
 *
 * Weather skill wiring: callers may pass `weatherSkillScalar` as the
 * compact §15 weather-skill row resolved for the active weather. It is
 * a number rather than an adjusted modifiers object so the 60 Hz race
 * loop does not allocate one object per AI per tick.
 *
 * Visibility-risk wiring: callers may pass a §14 low-visibility risk
 * scalar resolved from active weather. It multiplies deterministic
 * lane-target mistake odds so heavy weather and fog raise collision
 * risk through poorer read distance without changing hit geometry.
 *
 * Out of scope for this slice (deferred to follow-up AI dots):
 * - Overtake / lane-shift behaviour. §15 lists it; an AI may still
 *   collide with the player. Collision avoidance lands with the
 *   full grid slice.
 * - AI nitro firing. Not implemented in this slice.
 * - Full passing logic with inside / outside pass preferences.
 * - Damage-aware rub avoidance and contact fairness scoring.
 */

import type {
  AIArchetype,
  AIDriver,
  CarBaseStats,
  WeatherOption,
} from "@/data/schemas";
import { CURVATURE_SCALE, ROAD_WIDTH, SEGMENT_LENGTH } from "@/road/constants";
import type { CompiledSegmentBuffer } from "@/road/trackCompiler";
import type { CpuDifficultyModifiers } from "./aiDifficulty";
import { getAIBehaviour } from "./aiArchetypes";
import { decideFireNitro } from "./aiNitroFire";
import { NEUTRAL_INPUT, type Input } from "./input";
import { INITIAL_NITRO_STATE, type NitroState } from "./nitro";
import type { CarState } from "./physics";
import type { RaceState } from "./raceState";
import { deserializeRng } from "./rng";
import { WEATHER_VISIBILITY_RISK_MAX_SCALAR } from "./weather";

/**
 * Legacy identity CPU modifier row used as the default when a caller
 * does not supply tier-resolved §23 scalars. Each scalar is `1.0` so
 * the per-driver `paceScalar`, `mistakeRate`, and light recovery term
 * pass through unchanged for any caller that has not yet adopted the
 * tier wiring.
 */
export const IDENTITY_CPU_MODIFIERS: CpuDifficultyModifiers = Object.freeze({
  paceScalar: 1,
  recoveryScalar: 1,
  mistakeScalar: 1,
});

/**
 * Per-AI runtime state. Pinned by the dot stress-test:
 *
 * - `progress`: scalar position along the lap spline (compiled-segment
 *   index, fractional). Derived from `aiCar.z / SEGMENT_LENGTH` each
 *   tick; carried in state so future archetypes can detect lap
 *   transitions (intent changes, defensive lane swaps) without re
 *   computing across calls.
 * - `laneOffset`: signed lateral position in meters, mirroring `aiCar.x`.
 *   Carried so future overtake logic can reference last-tick offset
 *   without poking into the physics state.
 * - `speed`: current speed in m/s, mirroring `aiCar.speed`.
 * - `intent`: high-level driving mode per §15. The clean_line slice
 *   never leaves "conserve" but the field exists so other archetypes
 *   can flip without a schema change.
 * - `targetSpeed`: the requested speed for this tick, useful for
 *   telemetry overlays and for the throttle/brake controller.
 * - `seed`: per-AI PRNG channel for archetypes that randomise mistakes
 *   or nitro decisions. clean_line ignores this field but it must be
 *   present so adding it later does not break any caller.
 */
export interface AIState {
  progress: number;
  laneOffset: number;
  speed: number;
  intent: "defend" | "overtake" | "recover" | "conserve";
  targetSpeed: number;
  seed: number;
  readabilityCue: AIReadabilityCue;
}

export type AIReadabilityCue =
  | "clean-line"
  | "rocket-launch"
  | "rocket-fade"
  | "bully-pressure"
  | "cautious-low-visibility"
  | "chaotic-missed-apex"
  | "chaotic-brilliant"
  | "enduro-consistent"
  | "overtake";

/**
 * Initial AI state convenience: stationary at the start of the track
 * with `conserve` intent and a fresh seed. Callers building a grid
 * should override `seed` per slot so future PRNG-driven archetypes
 * decorrelate.
 */
export const INITIAL_AI_STATE: Readonly<AIState> = Object.freeze({
  progress: 0,
  laneOffset: 0,
  speed: 0,
  intent: "conserve",
  targetSpeed: 0,
  seed: 1,
  readabilityCue: "clean-line",
});

/**
 * Tunable constants for the clean_line archetype. Pinned in the dot
 * stress-test (items 4 through 7) so the controller is fully specified
 * without re-reading the GDD. Future archetypes will fork their own
 * constants object; this one stays the clean_line baseline.
 */
export const AI_TUNING = Object.freeze({
  /**
   * Maximum lateral offset for the racing-line approximation, in meters.
   * The AI biases toward the inside of curves up to this distance from
   * centerline. Capped at 70 percent of the road half-width so the AI
   * keeps a small margin to the rumble.
   */
  MAX_RACING_LINE_OFFSET: ROAD_WIDTH * 0.7,
  /**
   * Curve-driven deceleration coefficient. A unit-curvature turn cuts
   * target speed by this fraction before the per-driver `paceScalar`.
   * Tuned so a typical |curve|=0.5 segment trims ~30 percent off top.
   */
  CLEAN_LINE_CURVE_DECEL: 0.6,
  /**
   * Floor on AI target speed in m/s. Prevents pathological corners from
   * forcing the AI to a complete stop, which would visually look broken.
   */
  MIN_AI_SPEED: 8,
  /**
   * Distance (m) over which the AI blends from "hold spawn lane" to
   * "pursue racing line." On the §7 grid, all 12 cars start in three
   * staggered lanes; without this blend, every AI immediately steers
   * toward the centerline-anchored racing line and the field collides
   * into a pile-up before lap 1 even develops. Linear blend from 0 m
   * (hold lane) to LAUNCH_LANE_HOLD_M (full racing line).
   */
  LAUNCH_LANE_HOLD_M: 200,
  /**
   * Speed-error band around the target where the AI cruises. Inside
   * this band the AI feathers the throttle proportional to the error
   * sign, eliminating high-frequency bang-bang oscillation.
   */
  SPEED_HYSTERESIS: 1.5,
  /**
   * Speed overshoot at which the AI applies full brake. Below this the
   * brake input ramps linearly with the overshoot.
   */
  BRAKE_RAMP: 6,
  /**
   * Lateral error at which the AI applies full steer. Inside this band
   * the steer input ramps linearly with the lateral error. A P
   * controller is sufficient for clean_line; later archetypes may add
   * a damping term.
   */
  STEER_GAIN: 1.5,
  /**
   * Maximum lane-target offset injected by a deterministic mistake, in
   * meters. Kept below the road half-width so a mistake nudges the AI
   * toward a worse line without teleporting it off the road.
   */
  MAX_MISTAKE_OFFSET: ROAD_WIDTH * 0.35,
  /**
   * Maximum pace lift from the light catch-up term before the §23
   * `recoveryScalar` row is applied. The term only applies while the AI
   * trails the player and remains below the chassis top-speed clamp.
   */
  MAX_RECOVERY_PACE_BONUS: 0.05,
  /** Player gap, in meters, that reaches the maximum recovery term. */
  RECOVERY_GAP_FOR_MAX_BONUS: 240,
  /**
   * Inter-AI rubber-band compression cap before the §23
   * `recoveryScalar` row is applied. A trailing AI gets up to +5 %
   * pace toward the nearest peer ahead; a leading AI gets up to -5 %
   * toward the nearest peer behind. Easy / Normal scale this to keep
   * the §15 mid-pack churn observable; Hard / Master flatten it via
   * `recoveryScalar = 0.25 / 0`.
   */
  MAX_FIELD_COMPRESSION_PACE: 0.05,
  /**
   * Per-meter scale on the inter-AI compression. The followup spec
   * is `+/-3 % per 100 m`; the term is clamped at
   * `MAX_FIELD_COMPRESSION_PACE`.
   */
  FIELD_COMPRESSION_PER_METER: 0.0003,
  /**
   * Authored curve magnitude above which the overtake routine treats
   * the segment as a tight braking zone and prefers the inside line
   * (curve-direction side). `TrackSegmentSchema` keeps `curve` in
   * `[-1, 1]`; `0.4` selects the upper-third of cornering values
   * authored on real tracks.
   */
  OVERTAKE_BRAKING_CURVE_THRESHOLD: 0.4,
  /**
   * Lower bound on the sweeper window. Authored curves between this
   * value and `OVERTAKE_BRAKING_CURVE_THRESHOLD` are treated as
   * "in a sweeper" and pass on the outside line. Below this value
   * the segment counts as effectively straight and the routine falls
   * back to the easier-pass rule.
   */
  OVERTAKE_SWEEPER_CURVE_THRESHOLD: 0.1,
  /** Lap fraction where rocket starter launch boost stops applying. */
  ROCKET_LAUNCH_FRACTION: 0.18,
  /** Lap fraction where rocket starter late fade starts applying. */
  ROCKET_FADE_FRACTION: 0.72,
  /** Player gap where archetype lane pressure can engage. */
  TRAFFIC_PRESSURE_WINDOW_METERS: 36,
  /** Gap where a trailing AI can start a visible lane move. */
  OVERTAKE_WINDOW_METERS: 28,
  /** Lateral overtake target in meters. */
  OVERTAKE_LANE_SHIFT_METERS: 3,
  /** Minimum lateral space from the player target line during a pass. */
  OVERTAKE_PLAYER_MARGIN_METERS: 2,
  /**
   * Longitudinal range (m) where a same-lane car ahead triggers the
   * follow-distance throttle cap. Inside this window the trailing AI
   * targets the leader's speed instead of its own top, so contact band
   * (CAR_LENGTH_M = 4 m) stays empty. Tuned to give the trailer about a
   * second of braking room at race-pace speeds.
   */
  FOLLOW_DISTANCE_METERS: 14,
  /**
   * Lateral threshold (m) for "same lane" follow-distance detection. A
   * peer counts as a leader when its |dx| is below this value. Slightly
   * wider than CAR_WIDTH_M (1.8) so the throttle cap engages just before
   * lateral contact rather than the moment a car drifts in.
   */
  FOLLOW_LANE_THRESHOLD_METERS: 2.4,
  /**
   * Margin (m/s) the trailing AI keeps below the leader's speed inside
   * the follow window. Without it the trailer matches exactly and creeps
   * up on the leader; with too much, the trailer falls back unnecessarily.
   */
  FOLLOW_SPEED_BUFFER_M_PER_S: 1,
});

/**
 * Track context the AI needs from the renderer, mirroring physics's
 * `TrackContext`. Kept narrow so tests do not need to construct a
 * full `Track` to exercise the AI.
 */
export interface AITrackContext {
  roadHalfWidth: number;
}

/** Default context using the renderer's `ROAD_WIDTH` constant. */
export const DEFAULT_AI_TRACK_CONTEXT: Readonly<AITrackContext> = Object.freeze({
  roadHalfWidth: ROAD_WIDTH,
});

const EMPTY_THREATS: ReadonlyArray<OvertakeThreat> = Object.freeze([]);

/**
 * Minimal player view the AI consumes. Kept to one nested object now so
 * later overtake / collision-avoidance work can extend it without a
 * cascade of signature changes across all archetypes. The clean_line
 * slice does not actually read the player but takes the field so the
 * shape is stable.
 */
export interface PlayerView {
  car: Readonly<CarState>;
}

/** Output of one AI tick. */
export interface AITickResult {
  input: Input;
  nextAiState: AIState;
}

/**
 * Single AI tick. Pure: never mutates inputs; returns a fresh state and
 * a fresh `Input`. The result is the AI's contribution to the next
 * physics step.
 *
 * Edge cases handled here (per the dot's "Edge Cases" section):
 * - `race.phase !== "racing"`: returns `NEUTRAL_INPUT` and an updated
 *   state that still mirrors the AI's car position, so telemetry
 *   overlays can show the grid before the lights go green.
 * - AI off-track: the steer P-controller naturally pulls the AI back
 *   toward `idealLateralOffset = 0` once it crosses the rumble (because
 *   the curve sign at that local segment is small relative to the
 *   lateral error). No separate recovery state is required for the
 *   clean_line slice.
 * - Empty compiled track: fall through to a straight-line target with
 *   curve = 0. Defends against a degenerate compile result; the dot's
 *   spec keeps tests from constructing one but the guard is cheap.
 */
export function tickAI(
  driver: Readonly<AIDriver>,
  aiState: Readonly<AIState>,
  aiCar: Readonly<CarState>,
  player: Readonly<PlayerView>,
  track: Readonly<CompiledSegmentBuffer>,
  race: Readonly<RaceState>,
  stats: Readonly<CarBaseStats>,
  context: Readonly<AITrackContext> = DEFAULT_AI_TRACK_CONTEXT,
  _dt: number = 0,
  cpuModifiers: Readonly<CpuDifficultyModifiers> = IDENTITY_CPU_MODIFIERS,
  weatherSkillScalar: number = 1,
  visibilityRiskScalar: number = 1,
  aiNitro: Readonly<NitroState> = INITIAL_NITRO_STATE,
  weather: WeatherOption | null = null,
  /**
   * Other AI cars on the field this tick, in pre-physics positions.
   * `tickAI` reads them only as overtake targets so the AI can attempt
   * to pass slower cars in the pack instead of trailing them. Empty
   * array (or omitted) preserves the player-only overtake behaviour.
   */
  otherAiCars: ReadonlyArray<OvertakeThreat> = EMPTY_THREATS,
): AITickResult {
  const behaviour = getAIBehaviour(driver.archetype);
  const segment = currentSegment(track, aiCar.z);
  // `segment.curve` is the per-compiled-segment dx contribution, already
  // divided by `CURVATURE_SCALE` by the track compiler. The AI reasons
  // about the authored curvature instead (range -1..1 per the
  // `TrackSegmentSchema`) so the `MAX_RACING_LINE_OFFSET` and
  // `CLEAN_LINE_CURVE_DECEL` constants read against the same magnitude
  // a track author would type.
  const authoredCurve = segment ? segment.curve * CURVATURE_SCALE : 0;

  // Racing line: bias toward the inside of the curve. §15 "Track defines
  // a center path and suggested racing line bias curves" is approximated
  // here from the segment curve directly because no per-segment racing
  // line bias field exists in the data schema yet. When that schema
  // lands, replace this with `segment.racingLineBias`. The leading `0 +`
  // converts a `-0` from the multiplication on a perfectly straight
  // segment back to `+0` so call-sites can `expect(steer).toBe(0)`.
  const rawIdealOffset =
    -authoredCurve *
    AI_TUNING.MAX_RACING_LINE_OFFSET *
    behaviour.racingLineScalar;
  const trafficLaneOffset = trafficPressureOffset(
    behaviour.trafficLanePressure,
    driver.aggression,
    aiCar,
    player.car,
    context.roadHalfWidth,
  );
  const overtakeTarget = pickOvertakeTarget(aiCar, [
    player.car,
    ...otherAiCars,
  ]);
  const overtake = overtakeTarget
    ? overtakeOffset(
        aiCar,
        overtakeTarget,
        context.roadHalfWidth,
        behaviour.passMarginScalar,
        behaviour.prefersContextPasses ? authoredCurve : 0,
      )
    : { active: false, offset: 0 };
  // Launch-phase lane discipline: blend the racing-line target toward the
  // spawn lane (held in `aiCar.x` because the car has barely steered) so
  // the §7 grid can spread before each car commits to the centerline. The
  // overtake offset is held OUT of the blend so AI cars can still pass
  // each other during launch instead of rear-ending whoever is ahead.
  const racingLineComponent = rawIdealOffset + trafficLaneOffset;
  const launchBlend = clamp(aiCar.z / AI_TUNING.LAUNCH_LANE_HOLD_M, 0, 1);
  const idealOffsetWithTraffic =
    launchBlend * racingLineComponent +
    (1 - launchBlend) * aiCar.x +
    overtake.offset;
  const baseIdealLateralOffset = clamp(
    idealOffsetWithTraffic === 0 ? 0 : idealOffsetWithTraffic,
    -context.roadHalfWidth,
    context.roadHalfWidth,
  );
  const effectiveMistakeRate = clamp(
    driver.mistakeRate *
      behaviour.mistakeScalar *
      cpuModifiers.mistakeScalar *
      clamp(visibilityRiskScalar, 1, WEATHER_VISIBILITY_RISK_MAX_SCALAR),
    0,
    1,
  );
  let nextSeed = aiState.seed;
  let mistakeOffset = 0;
  let brilliantPaceBonus = 0;
  if (
    race.phase === "racing" &&
    (effectiveMistakeRate > 0 || behaviour.brilliantChance > 0)
  ) {
    const mistakeRng = deserializeRng(aiState.seed);
    const mistakeActive = mistakeRng.nextBool(effectiveMistakeRate);
    const mistakeDirection = mistakeRng.next() < 0.5 ? -1 : 1;
    mistakeOffset = mistakeActive
      ? mistakeDirection * AI_TUNING.MAX_MISTAKE_OFFSET
      : 0;
    if (mistakeRng.nextBool(behaviour.brilliantChance)) {
      brilliantPaceBonus = behaviour.brilliantPaceBonus;
    }
    nextSeed = mistakeRng.state;
  }
  const idealLateralOffset = clamp(
    baseIdealLateralOffset + mistakeOffset,
    -context.roadHalfWidth,
    context.roadHalfWidth,
  );

  const playerLeadMeters = Math.max(0, player.car.z - aiCar.z);
  const recoveryPaceBonus =
    clamp(
      playerLeadMeters / AI_TUNING.RECOVERY_GAP_FOR_MAX_BONUS,
      0,
      1,
    ) *
    AI_TUNING.MAX_RECOVERY_PACE_BONUS *
    cpuModifiers.recoveryScalar *
    behaviour.recoveryScalar;
  const fieldCompressionPaceBonus =
    fieldCompressionBonus(aiCar, otherAiCars) *
    cpuModifiers.recoveryScalar *
    behaviour.recoveryScalar;

  // Target speed per segment. §15 "AI chooses a lane offset target" plus
  // a per-driver `paceScalar` from §22 maps onto a curve-aware target
  // speed. The MIN_AI_SPEED floor keeps pathological corners drivable.
  // §23 "CPU difficulty modifiers" stacks the player-facing tier
  // `paceScalar` on top of the per-driver scalar so a clean_line driver
  // at Hard targets ~5% above the same driver at Normal, while at Easy
  // the same driver targets ~8% below. Identity modifiers (the default
  // when a caller has not threaded the resolved tier) preserve
  // pre-binding behaviour bit-for-bit. `weatherSkillScalar` stacks the
  // compact §15 weather-skill row without allocating adjusted modifier
  // objects per AI tick. The composed target is then re-clamped at
  // `stats.topSpeed` so a Master-tier driver with an authored
  // `paceScalar > 1` still cannot exceed the chassis ceiling.
  const curvePenalty =
    1 -
    AI_TUNING.CLEAN_LINE_CURVE_DECEL *
      visibilityCurveBrakeScalar(
        behaviour.curveBrakeScalar,
        behaviour.lowVisibilityBrakeScalar,
        visibilityRiskScalar,
      ) *
      Math.abs(authoredCurve);
  const lapProgressFraction = lapFraction(track.totalLength, aiCar.z);
  const launchPaceBonus =
    lapProgressFraction < AI_TUNING.ROCKET_LAUNCH_FRACTION
      ? behaviour.launchPaceBonus
      : 0;
  const fadePacePenalty =
    lapProgressFraction > AI_TUNING.ROCKET_FADE_FRACTION
      ? behaviour.fadePacePenalty
      : 0;
  const composedPaceScalar =
    driver.paceScalar *
    behaviour.targetSpeedScalar *
    cpuModifiers.paceScalar *
    clamp(weatherSkillScalar, 0, 2) *
    (1 + launchPaceBonus + brilliantPaceBonus - fadePacePenalty);
  const rawTarget =
    stats.topSpeed *
    Math.max(0, curvePenalty) *
    composedPaceScalar *
    (1 + recoveryPaceBonus + fieldCompressionPaceBonus);
  // Follow-distance throttle: if a car is close ahead in roughly the same
  // lane, cap the target at the leader's speed so the AI lifts off instead
  // of creeping into rear-end contact. Pairs with the carHit damage scan -
  // staying out of the contact band keeps cars from accumulating wreck-
  // threshold damage. Cars wanting to pass already get a non-zero
  // `overtake.offset` and will steer around the leader; this cap covers
  // the straight-line case where the trailer has no room to step out yet.
  const followCap = followDistanceCap(aiCar, [player.car, ...otherAiCars]);
  const cappedTarget = Math.min(rawTarget, followCap);
  const targetSpeed = clamp(cappedTarget, AI_TUNING.MIN_AI_SPEED, stats.topSpeed);

  // The state we will return regardless of phase. Mirrors the car so
  // other systems can read AI position without poking the physics state.
  const nextAiState: AIState = {
    progress: aiCar.z / SEGMENT_LENGTH,
    laneOffset: aiCar.x,
    speed: aiCar.speed,
    intent: overtake.active ? "overtake" : aiState.intent === "overtake" ? "recover" : aiState.intent,
    targetSpeed,
    seed: nextSeed,
    readabilityCue: readabilityCueFor({
      archetype: driver.archetype,
      launchPaceBonus,
      fadePacePenalty,
      trafficLaneOffset,
      visibilityRiskScalar,
      authoredCurve,
      mistakeOffset,
      brilliantPaceBonus,
      overtakeActive: overtake.active,
    }),
  };

  // Countdown: do not integrate inputs. Per the dot stress-test item 10
  // the AI returns `NEUTRAL_INPUT` and waits. The fresh state still
  // mirrors `aiCar` so a stationary grid renders correctly.
  if (race.phase !== "racing") {
    return { input: { ...NEUTRAL_INPUT }, nextAiState };
  }

  // Throttle / brake from speed error. Three regimes:
  // - error > +hysteresis: full throttle, no brake.
  // - error < -hysteresis: no throttle, brake proportional to overshoot.
  // - within band: proportional throttle, no brake.
  const speedError = targetSpeed - aiCar.speed;
  let throttle = 0;
  let brake = 0;
  if (speedError > AI_TUNING.SPEED_HYSTERESIS) {
    throttle = 1;
  } else if (speedError < -AI_TUNING.SPEED_HYSTERESIS) {
    brake = clamp(-speedError / AI_TUNING.BRAKE_RAMP, 0, 1);
  } else {
    throttle = clamp(speedError / AI_TUNING.SPEED_HYSTERESIS, 0, 1);
  }

  // Steer from lateral error. P-controller with a 1.5 m authority band.
  // Off-track recovery is implicit: once `aiCar.x` crosses the rumble
  // the lateral error grows and pulls the AI back toward the racing
  // line.
  const lateralError = idealLateralOffset - aiCar.x;
  const steer = clamp(lateralError / AI_TUNING.STEER_GAIN, -1, 1);

  // Derive the AI's own lap from its z accumulator. `race.lap` is the
  // player's counter; AI cars run independent lap counters at the
  // session level (`RaceSessionAICar.lap`) and in track-progress
  // space here we compute it directly from `aiCar.z` so this module
  // does not need a session-shape dependency.
  const aiLap =
    track.totalLength > 0
      ? Math.floor(aiCar.z / track.totalLength) + 1
      : 1;

  const fireNitro = decideFireNitro({
    archetype: driver.archetype,
    nitroUsage: driver.nitroUsage,
    nitro: aiNitro,
    seed: aiState.seed,
    aiSpeed: aiCar.speed,
    topSpeed: stats.topSpeed,
    authoredCurve,
    lap: aiLap,
    totalLaps: race.totalLaps,
    lapFraction: lapProgressFraction,
    playerGapMeters: player.car.z - aiCar.z,
    weather,
  });

  const input: Input = {
    steer,
    throttle,
    brake,
    nitro: fireNitro,
    handbrake: false,
    pause: false,
    shiftUp: false,
    shiftDown: false,
  };

  return { input, nextAiState };
}

/**
 * Look up the compiled segment under the given world Z position.
 * Returns `undefined` when the track is empty so the caller can fall
 * back to a straight-line default. Wraps with modulo so AIs that drive
 * past the compiled length keep producing sensible decisions until the
 * race lifecycle catches up and ends the session.
 */
function currentSegment(
  track: Readonly<CompiledSegmentBuffer>,
  z: number,
): CompiledSegmentBuffer["segments"][number] | undefined {
  const segments = track.segments;
  if (segments.length === 0) return undefined;
  const totalLength = track.totalLength;
  // Defend against zero or non-finite z; clamp into the ring.
  let normalized = z;
  if (!Number.isFinite(normalized)) normalized = 0;
  if (totalLength > 0) {
    normalized = ((normalized % totalLength) + totalLength) % totalLength;
  } else {
    normalized = 0;
  }
  const index = Math.min(
    segments.length - 1,
    Math.max(0, Math.floor(normalized / SEGMENT_LENGTH)),
  );
  return segments[index];
}

function lapFraction(totalLength: number, z: number): number {
  if (!Number.isFinite(totalLength) || totalLength <= 0) return 0;
  const normalized = ((z % totalLength) + totalLength) % totalLength;
  return clamp(normalized / totalLength, 0, 1);
}

/**
 * Inter-AI lead compression. Returns a signed pace bonus before the
 * §23 `recoveryScalar` row scales it: positive pulls a trailing AI
 * toward the pack, negative pulls a runaway leader back. The signed
 * gap to the closest peer ahead and the closest peer behind are
 * scaled by `FIELD_COMPRESSION_PER_METER` and clamped at
 * `MAX_FIELD_COMPRESSION_PACE`.
 *
 * Both ends are computed independently and summed so a mid-pack AI
 * with peers on both sides cancels out (the field stays compressed
 * around the median rather than collapsing to a point).
 */
function fieldCompressionBonus(
  aiCar: Readonly<CarState>,
  otherAiCars: ReadonlyArray<OvertakeThreat>,
): number {
  if (otherAiCars.length === 0) return 0;
  let nearestAheadGap = Number.POSITIVE_INFINITY;
  let nearestBehindGap = Number.POSITIVE_INFINITY;
  for (const peer of otherAiCars) {
    const gap = peer.z - aiCar.z;
    if (gap > 0 && gap < nearestAheadGap) nearestAheadGap = gap;
    else if (gap < 0 && -gap < nearestBehindGap) nearestBehindGap = -gap;
  }
  const aheadBonus = Number.isFinite(nearestAheadGap)
    ? clamp(
        nearestAheadGap * AI_TUNING.FIELD_COMPRESSION_PER_METER,
        0,
        AI_TUNING.MAX_FIELD_COMPRESSION_PACE,
      )
    : 0;
  const leaderPenalty = Number.isFinite(nearestBehindGap)
    ? clamp(
        nearestBehindGap * AI_TUNING.FIELD_COMPRESSION_PER_METER,
        0,
        AI_TUNING.MAX_FIELD_COMPRESSION_PACE,
      )
    : 0;
  return aheadBonus - leaderPenalty;
}

function trafficPressureOffset(
  pressure: number,
  aggression: number,
  aiCar: Readonly<CarState>,
  playerCar: Readonly<CarState>,
  roadHalfWidth: number,
): number {
  if (pressure === 0) return 0;
  const gap = playerCar.z - aiCar.z;
  if (Math.abs(gap) > AI_TUNING.TRAFFIC_PRESSURE_WINDOW_METERS) return 0;
  const closeness =
    1 - Math.abs(gap) / AI_TUNING.TRAFFIC_PRESSURE_WINDOW_METERS;
  const direction = clamp(
    (playerCar.x - aiCar.x) / Math.max(roadHalfWidth, 1),
    -1,
    1,
  );
  return direction * pressure * clamp(aggression, 0, 1) * closeness;
}

/**
 * Minimal threat shape `overtakeOffset` reads. Any car with `x`, `z`,
 * and `speed` can serve as a pass target - the player or another AI.
 */
interface OvertakeThreat {
  readonly x: number;
  readonly z: number;
  readonly speed: number;
}

/**
 * Speed cap for the AI when another car sits close ahead in roughly
 * the same lane. Returns `+Infinity` when no leader qualifies so the
 * caller can `Math.min` it against the raw target unconditionally.
 *
 * "Same lane" is `|dx| < FOLLOW_LANE_THRESHOLD_METERS`. "Close ahead" is
 * `0 < dz < FOLLOW_DISTANCE_METERS`. The cap is the leader's speed
 * minus a small buffer so the trailer holds station rather than
 * matching exactly and grinding into the contact band.
 *
 * Pure: scans the input in order and picks the closest qualifying
 * leader so the result is deterministic.
 */
function followDistanceCap(
  aiCar: Readonly<CarState>,
  candidates: ReadonlyArray<OvertakeThreat>,
): number {
  let bestGap = Number.POSITIVE_INFINITY;
  let leaderSpeed = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const gap = candidate.z - aiCar.z;
    if (gap <= 0 || gap > AI_TUNING.FOLLOW_DISTANCE_METERS) continue;
    if (
      Math.abs(candidate.x - aiCar.x) >= AI_TUNING.FOLLOW_LANE_THRESHOLD_METERS
    )
      continue;
    if (gap < bestGap) {
      bestGap = gap;
      leaderSpeed = candidate.speed;
    }
  }
  if (!Number.isFinite(leaderSpeed)) return Number.POSITIVE_INFINITY;
  return Math.max(0, leaderSpeed - AI_TUNING.FOLLOW_SPEED_BUFFER_M_PER_S);
}

/**
 * Pick the closest car ahead of the AI within the overtake window
 * that the AI can actually pass (its closing speed must be at least
 * even with the threat). Returns `null` when no candidate qualifies.
 *
 * Candidates are scanned in input order and the closest qualifying
 * threat wins on ties so the function stays deterministic across
 * runs of the same field.
 */
function pickOvertakeTarget(
  aiCar: Readonly<CarState>,
  candidates: ReadonlyArray<OvertakeThreat>,
): OvertakeThreat | null {
  let best: OvertakeThreat | null = null;
  let bestGap = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const gap = candidate.z - aiCar.z;
    if (gap <= 0 || gap > AI_TUNING.OVERTAKE_WINDOW_METERS) continue;
    if (aiCar.speed + 1 < candidate.speed) continue;
    if (gap < bestGap) {
      bestGap = gap;
      best = candidate;
    }
  }
  return best;
}

/**
 * Pick the lateral side (`+1` for right of the target, `-1` for
 * left) the AI should pass on. The default rule is "more room" -
 * whichever side of the target has more clear road - but when an
 * authored curve is supplied the AI prefers the §15 racing-line
 * preferences:
 *
 *   - Tight curve (|curve| > `OVERTAKE_BRAKING_CURVE_THRESHOLD`):
 *     inside-pass-under-braking. Pass-side equals the sign of the
 *     curve so a right-hander selects the right side (the inside
 *     line is the shorter path).
 *   - Sweeper (|curve| in `[OVERTAKE_SWEEPER_CURVE_THRESHOLD,
 *     OVERTAKE_BRAKING_CURVE_THRESHOLD]`): outside-pass-in-sweepers.
 *     Pass-side is the negative of the sign of the curve so the AI
 *     swings wide and uses the natural arc.
 *   - Below the sweeper threshold (effectively straight): falls
 *     through to the more-room rule.
 *
 * The bully archetype passes `0` here at the call site so it
 * always falls through to the more-room rule.
 */
function pickOvertakePassSide(
  target: OvertakeThreat,
  authoredCurve: number,
): 1 | -1 {
  if (Number.isFinite(authoredCurve)) {
    const magnitude = Math.abs(authoredCurve);
    if (magnitude >= AI_TUNING.OVERTAKE_BRAKING_CURVE_THRESHOLD) {
      return authoredCurve > 0 ? 1 : -1;
    }
    if (magnitude >= AI_TUNING.OVERTAKE_SWEEPER_CURVE_THRESHOLD) {
      return authoredCurve > 0 ? -1 : 1;
    }
  }
  return target.x <= 0 ? 1 : -1;
}

function overtakeOffset(
  aiCar: Readonly<CarState>,
  target: OvertakeThreat,
  roadHalfWidth: number,
  passMarginScalar: number,
  /**
   * Authored curve under the AI on this tick. `0` selects the
   * easier-pass rule (whichever side has more room). A non-zero
   * value selects an inside or outside line based on the curve
   * magnitude. Bully archetypes pass `0` here so they ignore the
   * preference per §15.
   */
  authoredCurve: number,
): { active: boolean; offset: number } {
  // Effective pass margin scaled by the archetype's
  // `passMarginScalar`. Bully rubs more (< 1.0); cautious leaves
  // more room (> 1.0); the polite default is 1.0.
  const effectiveMargin = Math.max(
    0,
    AI_TUNING.OVERTAKE_PLAYER_MARGIN_METERS * passMarginScalar,
  );
  const passSide = pickOvertakePassSide(target, authoredCurve);
  const desiredTarget = target.x + passSide * effectiveMargin;
  const boundedTarget = clamp(
    desiredTarget,
    -roadHalfWidth + effectiveMargin,
    roadHalfWidth - effectiveMargin,
  );
  const directionalTarget =
    passSide * Math.min(AI_TUNING.OVERTAKE_LANE_SHIFT_METERS, roadHalfWidth);
  const lateral =
    Math.abs(boundedTarget - target.x) >= effectiveMargin
      ? boundedTarget
      : directionalTarget;
  return {
    active: true,
    offset: clamp(lateral - aiCar.x, -AI_TUNING.OVERTAKE_LANE_SHIFT_METERS, AI_TUNING.OVERTAKE_LANE_SHIFT_METERS),
  };
}

function visibilityCurveBrakeScalar(
  curveBrakeScalar: number,
  lowVisibilityBrakeScalar: number,
  visibilityRiskScalar: number,
): number {
  const visibilityPressure = clamp(visibilityRiskScalar - 1, 0, 1);
  return curveBrakeScalar * (1 + lowVisibilityBrakeScalar * visibilityPressure);
}

function readabilityCueFor(input: {
  archetype: AIArchetype;
  launchPaceBonus: number;
  fadePacePenalty: number;
  trafficLaneOffset: number;
  visibilityRiskScalar: number;
  authoredCurve: number;
  mistakeOffset: number;
  brilliantPaceBonus: number;
  overtakeActive: boolean;
}): AIReadabilityCue {
  // Archetype-specific cues take precedence over the generic overtake
  // cue. Once AI-vs-AI overtake awareness is wired, overtake fires
  // frequently against the pack and would crowd out the more specific
  // archetype reads (bully-pressure, rocket-launch, etc.) that the
  // §15 readability spec calls out by name.
  if (input.archetype === "nitro_burst") {
    if (input.launchPaceBonus > 0) return "rocket-launch";
    if (input.fadePacePenalty > 0) return "rocket-fade";
  }
  if (
    input.archetype === "aggressive" &&
    Math.abs(input.trafficLaneOffset) > 0.05
  ) {
    return "bully-pressure";
  }
  if (
    input.archetype === "defender" &&
    input.visibilityRiskScalar > 1.05 &&
    Math.abs(input.authoredCurve) > 0.05
  ) {
    return "cautious-low-visibility";
  }
  if (input.archetype === "wet_specialist") {
    if (input.mistakeOffset !== 0) return "chaotic-missed-apex";
    if (input.brilliantPaceBonus > 0) return "chaotic-brilliant";
  }
  if (input.archetype === "endurance") return "enduro-consistent";
  if (input.overtakeActive) return "overtake";
  return "clean-line";
}

// Numeric helpers ----------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
